import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { api } from "./ApiClient";

import { CookiesHelper } from "../helpers/cookies.helper";
import { AuthHelper } from "../helpers/auth.helper";

import { ESimulationKey } from "../types/entities.enum";
import { HMIGetReportsForCompleteData, ISimulationReportResponse } from "../types/services.interface";
import { IAnalysisReportForCompleteData } from "../types/services.interface";

export const useGetSimulationReport = (simulationKey: ESimulationKey | null) => {
    const isSessionTokenValid = AuthHelper.isSessionTokenValid().result;

    const queryFn = async (): Promise<ISimulationReportResponse> => {
        const token = CookiesHelper.get(`${process.env.REACT_APP__SESSION_TOKEN_NAME}`);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const res = await api.post(`/analyses/report/${simulationKey}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['simulation-report', simulationKey],
        queryFn,
        retry: false,
        enabled: isSessionTokenValid && !!simulationKey,
    });
};

interface IReportSSEResponse {
    currentStep: number;
    message: string;
    reportResponse: ISimulationReportResponse | null;
    isLoading: boolean;
    isError: boolean;
};
export const useReportSSE = (simulationKey: ESimulationKey | null): IReportSSEResponse => {
    const [currentStep, setCurrentStep] = useState(0);
    const [message, setMessage] = useState("");
    const [reportResponse, setReportResponse] = useState<ISimulationReportResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);

    useEffect(() => {
        const token = CookiesHelper.get(`${process.env.REACT_APP__SESSION_TOKEN_NAME}`);
        const es = new EventSource(`${process.env.REACT_APP_API_URL}/analyses/report/stream/${simulationKey}/${token}`);

        es.onmessage = (event) => {
            const data = JSON.parse(event.data);

            if (data.step) {
                setCurrentStep(data.step);
                setMessage(data.message);
            };
            if (data.done) {
                setReportResponse(data.report);
                setIsLoading(false);
                es.close();
            };
        };

        es.onerror = () => {
            es.close();
            setIsLoading(false);
            setIsError(true);
        };

        return () => es.close();
    }, [simulationKey]);

    return { currentStep, message, reportResponse, isLoading, isError };
};

export const useGetReportForCompleteData = (analysisId: number | null) => {
    const queryFn = async (): Promise<IAnalysisReportForCompleteData> => {
        const res = await api.get(`/analyses/complete-data/analysis/${analysisId}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['report-for-complete-data', analysisId],
        queryFn,
        retry: false,
        enabled: !!analysisId,
    });
};

export const useGetReportsForCompleteData = () => {
    const mutationFn = async (data: HMIGetReportsForCompleteData): Promise<IAnalysisReportForCompleteData[]> => {
        const res = await api.get(`/analyses/complete-data/analyses`, { params: { analysesIds: data.analysesIds } });
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsDownloading(true);
        },
        onSuccess: (response, variables) => {
            variables.successCallback(response);
        },
        onError: (error: any) => {
            window.alert(error?.response?.data?.message || 'Something went wrong.');
        },
    });
};